import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import SendMoney from "./SendMoney";
import {Box} from "@mui/material";
function _createMdxContent(props) {
  const _components = {
    h5: "h5",
    img: "img",
    p: "p",
    strong: "strong",
    ..._provideComponents(),
    ...props.components
  }, {ChapterTitle, ExternalLink, InsetColumn, LargeQuote, MoreInfoContent, MoreInfoPopover, PageSection, TwoColumn, TwoColumnItem, Video} = _components;
  if (!ChapterTitle) _missingMdxReference("ChapterTitle", true);
  if (!ExternalLink) _missingMdxReference("ExternalLink", true);
  if (!InsetColumn) _missingMdxReference("InsetColumn", true);
  if (!LargeQuote) _missingMdxReference("LargeQuote", true);
  if (!MoreInfoContent) _missingMdxReference("MoreInfoContent", true);
  if (!MoreInfoPopover) _missingMdxReference("MoreInfoPopover", true);
  if (!PageSection) _missingMdxReference("PageSection", true);
  if (!TwoColumn) _missingMdxReference("TwoColumn", true);
  if (!TwoColumnItem) _missingMdxReference("TwoColumnItem", true);
  if (!Video) _missingMdxReference("Video", true);
  return _jsxs(_Fragment, {
    children: [_jsx(ChapterTitle, {
      title: "Finance Impacts Everyone",
      chapterNumber: 1,
      customGradient: `linear-gradient(to bottom, transparent 0%, #D2F400 50%, transparent 100%)`
    }), "\n", _jsx(PageSection, {
      backgroundStyle: "transparent",
      children: _jsxs(TwoColumn, {
        align: "center",
        children: [_jsx(TwoColumnItem, {
          sx: {
            marginTop: 0
          },
          children: _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "/sections/01-finance-impacts-everyone/images/big-screen.gif",
              alt: ""
            })
          })
        }), _jsx(TwoColumnItem, {
          children: _jsx(LargeQuote, {
            author: "Cala",
            authorTitle: "User Interviewee",
            children: _jsx(_components.p, {
              children: "You feel like they're stealing your money. It's not transparent."
            })
          })
        })]
      })
    }), "\n", _jsxs(PageSection, {
      backgroundStyle: "transparent",
      children: [_jsxs(_components.p, {
        children: ["Harmful design patterns ", _jsx(ExternalLink, {
          href: "https://www.tandfonline.com/doi/full/10.1080/13600834.2025.2461958",
          children: "inhibit a person’s agency"
        }), ", and their ability to fully have choice, control and consent in regards to every aspect of their daily lives, from how they spend their money to how they make choices about their privacy. But, I’d like to argue something more here, that the harm is more nefarious, by impacting users asymmetrically. In ", _jsx(ExternalLink, {
          href: "https://arxiv.org/pdf/2101.04843",
          children: "a paper by Princeton University"
        }), " on harmful design patterns, they described how harmful design patterns impact user’s welfare by causing financial loss, invasion of privacy and creating cognitive burden. Similarly, University of Washington Law professor Ryan Calo echoed something similar; that harmful design patterns ", _jsx(ExternalLink, {
          href: "https://www.gwlr.org/wp-content/uploads/2014/10/Calo_82_41.pdf",
          children: "can result in three kinds of harm typologies"
        }), ": economic harm, privacy harm and vulnerability as autonomy harm. But, moreso, harmful design patterns impact users disproportionately as well. In a study conducted by the ", _jsx(ExternalLink, {
          href: "https://op.europa.eu/en/publication-detail/-/publication/606365bc-d58b-11ec-a95f-01aa75ed71a1/language-en",
          children: "European Union"
        }), ", they found that “vulnerable consumers were more likely to make inconsistent choices (50.89%) than average consumers (47.24%).” Vulnerable, in this context, was used to describe users navigating the web in a second or third language, older users or those newer to technology."]
      }), _jsx(_components.p, {
        children: "The same European Union study found that around 97% of the most popular apps and websites visited or used by European consumers had one or more harmful design patterns present.This same study found that if a user had a background in user experience design or had already heard the term ‘dark pattern’, that user could more easily identify harmful design patterns. A person should not have to have a background in digital design, or only navigate the web in their first language to be able to exercise their consent, and agency. But I present these harms to illustrate this pervasive problem: harmful design patterns are so incredibly common across the internet, impacting all users and their everyday lives. Harmful design patterns do increase cognitive burden, and add confusion to processes by elongating them, making important content small or difficult to access, or adding other unnecessary points of friction, that all impact users. When an already arduous process or complex area of knowledge is made even more confusing, the harm falls onto that user, and it can be even more destructive."
      }), _jsx(SendMoney, {}), _jsxs(TwoColumn, {
        align: "center",
        children: [_jsxs(TwoColumnItem, {
          children: [_jsx(_components.h5, {
            children: "Finance is one of a user’s most intimate, important, and often, confusing areas of their lives"
          }), _jsx(_components.p, {
            children: "This brings us to the question “why focus on finance and financial service platforms and harmful design patterns?” The answer is, how could we not? Finance is one of a user’s most intimate, important, and often, confusing areas of their lives, and that’s even before we get to the software and companies they are. Drawing on what could be described academically as ‘anecdotal data’ but these are real observations and conversations from our friends, communities and colleagues, which is finance is so difficult to understand, and most people we spoke to felt like they didn’t have enough guidance, advice or knowledge in navigating their own finances and financial futures. Finance can feel difficult to understand as there are so many moving components to it, like what are the best banks to bank with to save money, how is the best way to save money, what pension plans are open to me, how can freelancers and artists best save money, what are the best ways to send money internationally with low cost fees, what are normal fees for banking generally, when should I start investing in stocks, how much money do I need to invest in stocks, what should I invest in, is crypto a good way to make and save money, what’s the best app for budgeting when I’m underpaid and clients are delayed in fulfilling invoices, and the list goes on and on. Financial service platforms, including traditional banking, are wrapped up in this ecosystem of worries, concerns, questions, and advice our communities are seeking in regards to finance generally. Similar to navigating any tax, medical, or other bureaucratic systems, the design of any financial service platform is a part of this complex, emotional and very thorny part of a real user’s life, which is their money, and their financial habits."
          })]
        }), _jsx(TwoColumnItem, {
          children: _jsx(LargeQuote, {
            color: "purple",
            showQuotes: false,
            children: _jsx(_components.p, {
              children: "Why focus on finance and financial service platforms and harmful design patterns?"
            })
          })
        })]
      }), _jsx(LargeQuote, {
        author: "Paulina",
        authorTitle: "User Interviewee",
        image: "/sections/01-finance-impacts-everyone/images/chile-quote.svg",
        children: _jsx(_components.p, {
          children: "I'm not aware of all the things that you need to do to send money from Chile - They charge me\nbecause I change the money from pounds to dollars and then they charge me in dollars. So there is\na lot of fees included in the conversion that I am not aware."
        })
      }), _jsx(InsetColumn, {
        children: _jsxs(TwoColumn, {
          align: "center",
          children: [_jsxs(TwoColumnItem, {
            children: [_jsx(_components.h5, {
              children: "‘Unfriendly Design’"
            }), _jsxs(_components.p, {
              children: ["Any design on top of these domains is already going to be complex for users to understand, even with the best design and the most user friendly company. ", _jsx(ExternalLink, {
                href: "https://bklynlibrary.libguides.com/c.php?g=764192&p=7671003",
                children: "Traditional banks"
              }), ", for example, are balancing regulatory requirements and constraints, along with verifying all different types of information to protect customers from frauds, scams, and user error, like sending money to the wrong person. All of these constraints and concerns need to be packaged into a user friendly and navigable system designed for all different types of people and backgrounds, but it also has to disclose different types of liabilities and legal framing to the customers, as well. Even moreso, finance comes with its own set of technical jargon, often which can be opaque to users who are new to finance. For example, in one of our user testing sessions, one of our users, Cala, was sending money from her Chilean traditional bank account to a traditional bank in the US. “", _jsx(_components.strong, {
                children: "What is an ACH?” she wondered"
              }), ". Her bank asked for the receiver’s ACH number to be able to send the payment, but the interface didn’t explain what an ", _jsxs(MoreInfoPopover, {
                children: ["ACH", _jsxs(MoreInfoContent, {
                  link: "https://stripe.com/en-de/resources/more/ach-routing-numbers-explained",
                  linkText: "Stripe Resources",
                  children: [_jsx(_components.strong, {
                    children: "ACH"
                  }), " stands for \"Automated Clearing House\", which is a \"network that moves money and information electronically between banks and financial institutions within the United States. An ACH number is a type of routing number used specifically for electronic transactions such as direct deposits and bill payments"]
                })]
              }), " was. During the user interview, I interjected in an attempt to help clarify, saying it was either an account number or routing number but I couldn’t remember which. ", _jsx(_components.strong, {
                children: "A quick Google search explained it’s related to routing numbers"
              }), ". In this instance, Cala’s traditional bank in Chile could have utilized a small, clickable UI on their interface that when touched, could have opened a small interstitial explaining what an ACH was. This small change would be incredibly pro user, and could help in guiding Cala to more easily make the right decision within the banking app and not have to leave the app to seek more information. ", _jsx(_components.strong, {
                children: "While this is not a harmful design pattern, it is an example of ‘unfriendly design’ or bad design for the users"
              }), ". The interface is working against the user by assuming the user remembers specific financial jargon, as opposed to helping explain and support the user within the design of the interface itself."]
            })]
          }), _jsx(TwoColumnItem, {
            children: _jsx(Video, {
              src: "/sections/01-finance-impacts-everyone/videos/swift-1.mp4"
            })
          })]
        })
      }), _jsx(Box, {
        sx: {
          display: "flex",
          justifyContent: {
            xs: "center",
            md: "flex-end"
          },
          marginBottom: -6
        },
        children: _jsx(Box, {
          sx: {
            maxWidth: {
              xs: "80%",
              md: "33%"
            },
            width: "100%"
          },
          children: _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "/sections/01-finance-impacts-everyone/images/transfer.gif",
              alt: ""
            })
          })
        })
      }), _jsx(LargeQuote, {
        author: "Cala",
        authorTitle: "User Interviewee",
        fullWidth: true,
        children: _jsx(_components.p, {
          children: "I knew that I had to use certain codes, like the IBAN and the I forgot the names. But, like, I knew that I needed that input information for it to work. But the hardest part, I think would be just, like, the lack of transparency of how the calculations are done."
        })
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
